import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { ARTICLE_STATIC, SITE, buildWhatsAppLink } from "../data/static";

const formatDate = (iso, lang) => {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const renderBody = (body) =>
  body.map((block, i) => {
    if (block.type === "h2")
      return (
        <h2
          key={i}
          className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-10 mb-4 leading-tight"
        >
          {block.text}
        </h2>
      );
    if (block.type === "ul")
      return (
        <ul key={i} className="mt-2 mb-6 space-y-2">
          {block.items.map((it) => (
            <li key={it} className="flex items-start gap-2.5 text-base text-slate-700 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D1603D] shrink-0" />
              {it}
            </li>
          ))}
        </ul>
      );
    return (
      <p key={i} className="text-base sm:text-[17px] text-slate-700 leading-[1.75] mt-4">
        {block.text}
      </p>
    );
  });

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const b = t.blog;

  const article = useMemo(
    () => b.articles.find((a) => a.slug === slug),
    [b.articles, slug]
  );
  const stat = article ? ARTICLE_STATIC[article.slug] : null;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Jaipur Tours & Travels`;
    } else {
      document.title = b.notFound;
    }
    window.scrollTo(0, 0);
  }, [article, b.notFound]);

  // Inject Article JSON-LD for this post (rich-results)
  useEffect(() => {
    if (!article || !stat) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: stat.heroImage.startsWith("http")
        ? stat.heroImage
        : `https://jaipurtoursandtravels.in${stat.heroImage}`,
      datePublished: stat.publishedAt,
      author: { "@type": "Organization", name: article.author },
      publisher: {
        "@type": "Organization",
        name: SITE.name,
        logo: {
          "@type": "ImageObject",
          url: "https://jaipurtoursandtravels.in/favicon.ico",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://jaipurtoursandtravels.in/destinations/${article.slug}`,
      },
      inLanguage: lang === "hi" ? "hi-IN" : "en-IN",
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld", "article");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      document.head
        .querySelectorAll('script[data-jsonld="article"]')
        .forEach((el) => el.remove());
    };
  }, [article, stat, lang]);

  if (!article) {
    return (
      <div className="pt-32 pb-20 min-h-screen bg-[#FAFAF7]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="font-display text-2xl text-slate-700">{b.notFound}</p>
          <Link
            to="/destinations"
            data-testid="blog-not-found-cta"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D1603D] text-white text-sm font-semibold"
          >
            {b.notFoundCta}
          </Link>
        </div>
      </div>
    );
  }

  const waLink = buildWhatsAppLink(b.waMsg(article.title));
  const related = b.articles.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <article
      data-testid={`blog-post-${article.slug}`}
      className="pt-24 lg:pt-32 pb-20 bg-[#FAFAF7] min-h-screen"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/destinations"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#D1603D] hover:gap-3 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> {b.back}
        </Link>

        <header className="mt-6 lg:mt-8">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {b.publishedOn} {formatDate(stat.publishedAt, lang)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {stat.readMinutes} {b.readTime}
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-[1.1]">
            {article.title}
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        <div className="relative mt-8 lg:mt-10 aspect-[16/10] rounded-3xl overflow-hidden bg-slate-100">
          <img
            src={stat.heroImage}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>

        <div className="prose-jtt mt-10 lg:mt-12 first:mt-0">
          {renderBody(article.body)}
        </div>

        {/* Inline WhatsApp CTA */}
        <div className="mt-12 lg:mt-16 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#FADBD1]/50 via-[#FAFAF7] to-[#FDE68A]/30 border border-[#D1603D]/15">
          <p className="font-display text-xl sm:text-2xl font-semibold text-slate-900 leading-tight">
            {article.title}
          </p>
          <p className="mt-2 text-sm text-slate-600">{article.excerpt}</p>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-book-cta"
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D1603D] text-white text-sm font-semibold hover:bg-[#B34A2B] transition-colors"
          >
            {b.bookCta} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 lg:mt-24">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D] mb-6">
            {b.related}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {related.map((r) => {
              const rstat = ARTICLE_STATIC[r.slug];
              return (
                <Link
                  key={r.slug}
                  to={`/destinations/${r.slug}`}
                  data-testid={`related-card-${r.slug}`}
                  className="group flex gap-4 bg-white rounded-2xl overflow-hidden border border-slate-200/80 hover:border-[#D1603D]/40 transition-colors"
                >
                  <div className="w-32 sm:w-40 h-32 sm:h-36 shrink-0 overflow-hidden bg-slate-100">
                    <img
                      src={rstat.heroImage}
                      alt={r.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 p-4 pr-5 flex flex-col justify-center">
                    <p className="font-display text-base sm:text-lg font-semibold text-slate-900 leading-snug group-hover:text-[#D1603D] transition-colors line-clamp-3">
                      {r.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      {rstat.readMinutes} {b.readTime}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}
